/**
 * FeaturesPage - página detalhada de funcionalidades
 */
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
    Bot,
    Calendar,
    BarChart3,
    Megaphone,
    Image as ImageIcon,
    MessageSquare,
    Repeat,
    Settings,
    Globe,
    Sparkles,
    Hash,
    Lightbulb,
} from "lucide-react";
import { CHANNELS } from "@/lib/channels";

const SECTIONS = [
    {
        title: "Inteligencia Artificial integrada",
        items: [
            { icon: MessageSquare, title: "Legendas automáticas", desc: "Em português de Portugal, no tom da sua marca." },
            { icon: Hash, title: "Hashtags relevantes", desc: "Mistura de tags populares e nicho local angolano." },
            { icon: Lightbulb, title: "Ideias de conteúdo", desc: "Sugestões de Reels, Carrosseis e Stories por nicho." },
            { icon: ImageIcon, title: "Geração de imagens", desc: "Imagens 1080x1080 prontas a publicar, sem stock fotos." },
        ],
    },
    {
        title: "Gestão de publicações",
        items: [
            { icon: Calendar, title: "Calendário visual", desc: "Mensal, semanal e diário. Arraste para reagendar." },
            { icon: Repeat, title: "Publicação automática", desc: "Definida a hora? Nós publicamos por si." },
            { icon: Globe, title: "Multi-canal", desc: "Uma publicação, todas as redes — com pré-visualização por canal." },
            { icon: Settings, title: "Estado claro", desc: "Rascunho, Agendado, Publicado ou Falhado — tudo à vista." },
        ],
    },
    {
        title: "Análises e Campanhas",
        items: [
            { icon: BarChart3, title: "Relatórios completos", desc: "Alcance, engagement, melhores horas e top posts." },
            { icon: Megaphone, title: "Campanhas Ads", desc: "Orçamento, audiência, criativos e métricas." },
            { icon: Sparkles, title: "Insights de IA", desc: "Recomendações para melhorar performance." },
            { icon: Bot, title: "Brand voice", desc: "A IA aprende a voz da sua marca ao longo do tempo." },
        ],
    },
];

export default function FeaturesPage() {
    return (
        <div data-testid="features-page">
            <section className="bg-hero-radial-light">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <Badge variant="outline" className="mb-4 border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#6B21D9]">
                        Funcionalidades
                    </Badge>
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                        Uma plataforma. <br />
                        <span className="text-[#6B21D9]">Todas as redes.</span>
                    </h1>
                    <p className="mt-4 max-w-xl text-muted-foreground">
                        Conteúdo, agendamento, análises e anúncios. Tudo o que um Social Media
                        precisa, ao alcance de um clique.
                    </p>
                </div>
            </section>

            {/* Channels supported */}
            <section className="py-12 border-y border-border bg-muted/30">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                    <h3 className="font-display text-xl font-semibold mb-6">Canais suportados</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {CHANNELS.map((c) => {
                            const Icon = c.icon;
                            return (
                                <Card
                                    key={c.type}
                                    className="p-4 flex flex-col items-center text-center gap-2 bg-card border-border"
                                >
                                    <span
                                        className={`h-10 w-10 rounded-xl ${c.bg} text-white grid place-items-center`}
                                    >
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <span className="text-sm font-medium">{c.name}</span>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {SECTIONS.map((s) => (
                <section key={s.title} className="py-14 sm:py-20">
                    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                        <h2 className="font-display text-3xl font-semibold tracking-tight mb-8">
                            {s.title}
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                            {s.items.map((it) => {
                                const Icon = it.icon;
                                return (
                                    <Card key={it.title} className="p-5 rounded-2xl bg-card border-border">
                                        <div className="h-10 w-10 rounded-xl bg-[#6B21D9]/10 grid place-items-center text-[#6B21D9] mb-3">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h3 className="font-semibold">{it.title}</h3>
                                        <p className="mt-1 text-sm text-muted-foreground">{it.desc}</p>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>
            ))}

            <section className="py-16 bg-muted/30">
                <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-3xl font-semibold tracking-tight">
                        Comece o seu piloto automático hoje.
                    </h2>
                    <p className="mt-3 text-muted-foreground">7 dias grátis, sem cartão de crédito.</p>
                    <Button asChild size="lg" className="mt-6 btn-grad-purple text-white h-12 px-6">
                        <Link to="/registo">Experimentar grátis</Link>
                    </Button>
                </div>
            </section>
        </div>
    );
}
